import { baseApi } from './baseApi';
import { mapAssetToFolderManager, sortByDate } from '../helpers';
import { FolderManagerMimeTypes } from '../interfaces';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';

interface AssetItemFromApi {
  ID: number;
  Name: string;
  Type: string;
  Url: string;
  FolderID: number;
  SourceID: number;
  Source: AssetItemFromApi;
  Folder: AssetItemFromApi;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
}

const getThumbnailUrl = (item: AssetItemFromApi) => {
  if (
    item.Type === FolderManagerMimeTypes.PDF ||
    item.Type === FolderManagerMimeTypes.PPTX ||
    item.Type === FolderManagerMimeTypes.ZIP
  ) {
    return null;
  }
  return item.Url;
};

export const mapApiToStore = (item: AssetItemFromApi) => ({
  id: item.ID,
  name: item.Name,
  type: item.Type,
  url: item.Url,
  thumbnailUrl: getThumbnailUrl(item),
  createdAt: item.CreatedAt,
  updatedAt: item.UpdatedAt,
  deletedAt: item.DeletedAt,
  // relations
  folderId: item.FolderID,
  sourceId: item.SourceID,
  source: item.Source,
  folder: item.Folder,
});

export const assetsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createAsset: builder.mutation<void, ICreateAssetProps>({
      query: ({ data }) => ({
        url: `/assets`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { parentId }) => [{ type: 'Folders', id: `ASSETS_${parentId}` }],
    }),
    getAssets: builder.query<IApiResponse<{ error: string } | string>, { query: string }>({
      query: ({ query }) => ({
        url: `/assets${query}`,
      }),
      transformResponse: (result: any) => (result?.data || []).map(mapAssetToFolderManager),
      providesTags: ['Assets'],
      keepUnusedDataFor: 0,
    }),
    getAssetPresignedUrl: builder.query<IApiResponse<{ error: string } | string>, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/download`,
      }),
      keepUnusedDataFor: 0,
    }),
    getAssetVersionPresignedUrl: builder.query<
      IApiResponse<{ error: string } | string>,
      {
        assetId: number;
        versionId: number;
      }
    >({
      query: ({ assetId, versionId }) => ({
        url: `/assets/${assetId}/versions/${versionId}/download`,
      }),
      keepUnusedDataFor: 0,
    }),
    getAssetVersions: builder.query<IAssetVersion[], { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/versions`,
      }),
      transformResponse: (result: IApiResponse<IAssetVersion[]>) => sortByDate(result?.data || [], 'CreatedAt', 'desc'),
      providesTags: (result, error, { assetId }) => [
        { type: 'Assets', id: `GET_VERSIONS_URL_${assetId}` },
        {
          type: 'Assets',
          id: 'VERSIONS',
        },
      ],
    }),
    getRecentAssets: builder.query<FolderManagerItem[], void>({
      query: () => `/assets/me/recent`,
      transformResponse: (result: any) => (result.data || []).map(mapAssetToFolderManager),
      providesTags: (result, error) => [{ type: 'Assets', id: `RECENT` }, { type: 'Assets' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch recent assets!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          console.error(message, error);
        }
      },
    }),
    getAssetThumbnailUrl: builder.query<string, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/thumbnail`,
      }),
      transformResponse: (result: IApiResponse<string>) => result?.data,
      providesTags: (result, error, { assetId }) => [{ type: 'Assets', id: `GET_THUMBNAIL_URL_${assetId}` }],
      keepUnusedDataFor: 0,
    }),
    updateAsset: builder.mutation<IApiResponse<AssetItemFromApi>, IUpdateAssetProps>({
      query: ({ assetId, data }) => ({
        url: `/assets/${assetId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Assets' }, { type: 'Presentations' }],
    }),
    deleteAssetById: builder.mutation<void, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Presentations' }],
    }),
  }),
});

export const {
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useGetRecentAssetsQuery,
  useGetAssetVersionsQuery,
  useDeleteAssetByIdMutation,
  useGetAssetThumbnailUrlQuery,
  useLazyGetAssetPresignedUrlQuery,
  useLazyGetAssetVersionPresignedUrlQuery,
} = assetsService;
